import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useLanguage } from '../context/LanguageContext';
import { pageAPI } from '../services/api';
import { Globe } from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { language, setLanguage, languages, t } = useLanguage();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPublicationsOpen, setIsPublicationsOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isMobilePublicationsOpen, setIsMobilePublicationsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [navbarPages, setNavbarPages] = useState([]);

  useEffect(() => {
    pageAPI.getNavbarPages().then((res) => setNavbarPages(res.data)).catch(() => {});
  }, []);
  const publicationsRef = useRef(null);
  const aboutRef = useRef(null);
  const searchInputRef = useRef(null);

  // Publications (unchanged), Datasets and Podcast as direct links
  // (previously grouped under a merged "Explore" dropdown, but a
  // dropdown holding only one item after Podcast moved out to its
  // own standalone link just adds a click for nothing -- same
  // reasoning as About being a direct link elsewhere here),
  // Favorites folded into the Account/Profile menu instead of being
  // orphaned with no nav entry. Donate lives in the footer, not here
  // -- keeps the navbar focused on core navigation.
  const publicationsLinks = [
    { to: '/research', label: t('nav.research') },
    { to: '/publications/articles-journals', label: t('nav.ghanaianArticles') },
    { to: '/publications/current-affairs', label: t('nav.currentAffairs') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/research?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  useEffect(() => {
    if (isSearchOpen) searchInputRef.current?.focus();
  }, [isSearchOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (publicationsRef.current && !publicationsRef.current.contains(event.target)) {
        setIsPublicationsOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(event.target)) {
        setIsAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkClass = "text-gray-900 hover:text-acar-crimson transition-colors duration-150";

  return (
    <nav className="bg-acar-cream text-gray-900 border-b border-acar-cream-border">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-2">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt={settings.site_name} className="h-16 w-auto object-contain" />
            ) : (
              <svg className="h-16 w-16 text-acar-crimson" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
              </svg>
            )}
            <div className="leading-tight">
              <div className="text-xl font-medium">{settings.site_name}</div>
              {settings.tagline && (
                <div className="text-xs text-gray-500 hidden sm:block">{settings.tagline}</div>
              )}
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Publications dropdown */}
            <div className="relative" ref={publicationsRef}>
              <button
                onClick={() => setIsPublicationsOpen(!isPublicationsOpen)}
                className={`flex items-center gap-1 ${linkClass}`}
                aria-expanded={isPublicationsOpen}
              >
                {t('nav.publications')}
                <svg className={`w-4 h-4 transition-transform ${isPublicationsOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>

              {isPublicationsOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-acar-cream border border-acar-cream-border shadow-lg py-2 z-50">
                  {publicationsLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="block px-4 py-2 text-gray-900 hover:text-acar-crimson hover:bg-acar-cream-hover"
                      onClick={() => setIsPublicationsOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Datasets -- direct link, not a dropdown, now that
                Podcast has moved out to its own standalone link (a
                dropdown with one item just adds a click for nothing,
                same reasoning as the About link elsewhere here) */}
            <Link to="/datasets" className={linkClass}>{t('nav.exploreDatasets')}</Link>

            <Link to="/download-data" className={linkClass}>{t('nav.downloadData')}</Link>
            <Link to="/open-book" className={linkClass}>{t('nav.openBook')}</Link>

            {/* About dropdown -- About Us and Company Profile are both
                dedicated singleton pages now (mirrors of each other),
                not flexible Pages -- see CompanyProfile.js and
                /api/company-profile/. Placed last, after the content
                sections -- institutional/reference info conventionally
                sits at the end of a navbar, not competing for
                attention with what the site actually offers. */}
            <div className="relative" ref={aboutRef}>
              <button
                onClick={() => setIsAboutOpen(!isAboutOpen)}
                className={`flex items-center gap-1 ${linkClass}`}
                aria-expanded={isAboutOpen}
              >
                {t('nav.about')}
                <svg className={`w-4 h-4 transition-transform ${isAboutOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>

              {isAboutOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-acar-cream border border-acar-cream-border shadow-lg py-2 z-50">
                  <Link to="/about" className="block px-4 py-2 text-gray-900 hover:text-acar-crimson hover:bg-acar-cream-hover"
                    onClick={() => setIsAboutOpen(false)}>
                    {t('nav.about')}
                  </Link>
                  <Link to="/company-profile" className="block px-4 py-2 text-gray-900 hover:text-acar-crimson hover:bg-acar-cream-hover"
                    onClick={() => setIsAboutOpen(false)}>
                    {t('nav.companyProfile')}
                  </Link>
                </div>

              )}
            </div>

            {/* Admin-created flexible pages flagged to show here --
                appear automatically with no code change, per
                Page.show_in_navbar */}
            {navbarPages.map((page) => (
              <Link key={page.id} to={`/pages/${page.slug}`} className={linkClass}>{page.title}</Link>
            ))}

            {/* Language switcher -- swaps in translated content where an
                admin has generated it, with English as the fallback */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className={`flex items-center gap-1 ${linkClass}`}
                aria-expanded={isLanguageOpen}
                aria-label="Change language"
                title={languages.find((l) => l.code === language)?.native}
              >
                <Globe size={18} />
                <svg className={`w-3 h-3 transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
              {isLanguageOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-acar-cream border border-acar-cream-border shadow-lg py-2 z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLanguage(l.code); setIsLanguageOpen(false); }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-acar-cream-hover
                        ${language === l.code ? 'text-acar-crimson font-semibold' : 'text-gray-900'}`}
                    >
                      {l.native}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search -- expands into a real, working search input */}
            <div className="relative flex items-center">
              {isSearchOpen ? (
                <form onSubmit={handleSearchSubmit} className="flex items-center">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={() => { if (!searchQuery) setIsSearchOpen(false); }}
                    placeholder="Search research..."
                    className="border border-acar-cream-border bg-acar-cream rounded px-3 py-1.5 text-sm w-40 focus:outline-none focus:ring-1 focus:ring-acar-crimson"
                  />
                </form>
              ) : (
                <button onClick={() => setIsSearchOpen(true)} aria-label="Search" className={linkClass}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              )}
            </div>

            {(user?.is_staff || user?.role === 'admin') && (
              <Link to="/admin/dashboard" className={linkClass}>
                <svg className="w-5 h-5 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                </svg>
                {t('nav.dashboard')}
              </Link>
            )}

            {isAuthenticated ? (
              /* Account dropdown -- Profile, Favorites, Logout */
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className={`flex items-center space-x-2 ${linkClass}`}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                  </svg>
                  <span>{user?.username}</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-acar-cream border border-acar-cream-border shadow-lg py-2 z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-gray-900 hover:text-acar-crimson hover:bg-acar-cream-hover"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      {t('nav.profile')}
                    </Link>
                    <Link
                      to="/favorites"
                      className="block px-4 py-2 text-gray-900 hover:text-acar-crimson hover:bg-acar-cream-hover"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      {t('nav.favorites')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-gray-900 hover:text-acar-crimson hover:bg-acar-cream-hover"
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className={linkClass}>
                {t('nav.login')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              {isMenuOpen ? (
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
              ) : (
                <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"/>
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden pb-4 space-y-1">
            <Link to="/" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
              {t('nav.home')}
            </Link>

            {/* Publications, expandable */}
            <div>
              <button
                onClick={() => setIsMobilePublicationsOpen(!isMobilePublicationsOpen)}
                className="w-full flex items-center justify-between py-2 text-gray-900 hover:text-acar-crimson transition-colors"
              >
                {t('nav.publications')}
                <svg className={`w-4 h-4 transition-transform ${isMobilePublicationsOpen ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
              {isMobilePublicationsOpen && (
                <div className="pl-4 space-y-1 border-l border-acar-cream-border ml-1">
                  {publicationsLinks.map(link => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="block py-2 text-sm text-gray-900 hover:text-acar-crimson transition-colors"
                      onClick={() => { setIsMenuOpen(false); setIsMobilePublicationsOpen(false); }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Datasets -- direct link, not expandable, now that
                Podcast has moved out to its own standalone link */}
            <Link to="/datasets" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
              {t('nav.exploreDatasets')}
            </Link>

            <Link to="/download-data" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
              {t('nav.downloadData')}
            </Link>
            <Link to="/open-book" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
              {t('nav.openBook')}
            </Link>

            {/* About, placed last -- same reasoning as desktop */}
            <Link to="/about" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
              {t('nav.about')}
            </Link>
            <Link to="/company-profile" className="block py-2 pl-4 text-sm text-gray-700 hover:text-acar-crimson transition-colors"
              onClick={() => setIsMenuOpen(false)}>
              {t('nav.companyProfile')}
            </Link>

            {navbarPages.map((page) => (
              <Link key={page.id} to={`/pages/${page.slug}`}
                className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors"
                onClick={() => setIsMenuOpen(false)}>
                {page.title}
              </Link>
            ))}

            {user?.is_staff && (
              <Link to="/admin/dashboard" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
                <svg className="w-5 h-5 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                </svg>
                {t('nav.dashboard')}
              </Link>
            )}

            {isAuthenticated ? (
              <>
                <Link to="/profile" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
                  {t('nav.profile')}
                </Link>
                <Link to="/favorites" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
                  {t('nav.favorites')}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-gray-900 hover:text-acar-crimson transition-colors"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Link to="/login" className="block py-2 text-gray-900 hover:text-acar-crimson transition-colors" onClick={() => setIsMenuOpen(false)}>
                {t('nav.login')}
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
